
import React, { useState, useContext } from "react";
import { Formik, Form, FormikProps } from "formik";
import { Divider, Message, Container, Button, Form as SForm, Segment } from "@/components/bp/bp-ui";
import * as yup from "yup";
import { FormErrorList } from "../FormErrorList";
import { useTranslation2 } from "../Contexts/TranslationContext2";
import SemanticFormikInput from "@app/components/React/Common/Forms/SemanticFormikInput";
import SemanticFormikSelect from "@app/components/React/Common/Forms/SemanticFormikSelect";
import { PageHeader } from "@common/PageHeader";
import { userClient, roleClient, labClient, translationClient } from "@app/api2/Clients";
import { useQuery } from "@tanstack/react-query";
import { buildUrl, AppRoutes } from "../AppRoutes";
import { BPButton } from "@/components/bp/bpbutton";
import { BPContainer } from "@/components/bp/bpcontainer";

interface UserCreateFormData {
    name: string;
    email: string;
    role: string;
    lab: number;
    locale: string;
}

export const UserCreate: React.FC = () => {
    const [showServerErrors, setShowServerErrors] = useState(false);
    const [serverErrors, setServerErrors] = useState<string[]>([]);

    const { translations: t } = useTranslation2();

    // Validation Schema
    const UserCreateSchema: yup.SchemaOf<UserCreateFormData> = yup.object().shape({
        email: yup.string().email().required().label(t.common.emailAddress),
        name: yup.string().required().label(t.user.name),
        role: yup.string().required(t.user.role),
        locale: yup.string().required(t.common.language),
        lab: yup.number().required(t.common.lab),
    });

    // Data loading
    const { data, isLoading } = useQuery({
        queryKey: ['userCreate'],
        queryFn: async () => {
            const labs = await labClient.getAll();
            const roles = await roleClient.getAll();
            const locales = await translationClient.getLocales();
            return { labs, roles, locales };
        }
    });


    const labList = data?.labs?.labs?.map((l) => ({ text: l.name, value: l.id, key: l.id })) || [];
    const roleList = data?.roles?.map((r) => ({ text: r.name, value: r.name, key: r.name })) || [];
    const localeList = data?.locales?.map((l) => ({ text: l.name, value: l.code, key: l.code })) || [];

    // Initial values
    let initialLab = labList.length === 1 ? labList[0].value : 0;

    const initialValues: UserCreateFormData = {
        name: "",
        email: "",
        role: "",
        lab: initialLab,
        locale: ""
    };

    // Handlers
    const handleSubmit = async (values: UserCreateFormData) => {
        const result = await userClient.create({
            email: values.email,
            labs: [values.lab],
            name: values.name,
            role: values.role,
            locale: values.locale
        });
        if (result.success) {
            window.location.assign(buildUrl(AppRoutes.UserDetails, { userId: result.userId! }));
        } else {
            setServerErrors(result.errors);
            setShowServerErrors(true);
        }
    };

    // Renderers
    const renderForm = (formikProps: FormikProps<UserCreateFormData>) => {
        const errorList = Object.getOwnPropertyNames(formikProps.errors)
            .filter((k) => formikProps.touched[k])
            .map((k) => formikProps.errors[k]);
        return (
            <SForm as={Form} onSubmit={formikProps.handleSubmit}>
                <Segment loading={isLoading}>
                    <SemanticFormikInput name="name" label={t.user.name} required />
                    <SemanticFormikInput name="email" label={t.common.emailAddress} required />
                    <SemanticFormikSelect name="role" label={t.user.role} options={roleList} selection required />
                    <SemanticFormikSelect name="lab" label={t.common.lab} options={labList} selection required />
                    <SemanticFormikSelect name="locale" label={t.common.language} options={localeList} selection required />
                    <Divider />
                    <BPButton type="submit" disabled={!formikProps.isValid}>{t.common.save}</BPButton>
                    <Message error header={t.common.errorsOnForm} list={errorList} visible={errorList.length > 0} />
                    <FormErrorList message={t.user.errorCreating} show={showServerErrors} errors={serverErrors} />
                </Segment>
            </SForm>
        );
    };

    return (
        <BPContainer>
            <PageHeader header={window.translation.userView.create} />
            {!isLoading && (
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={UserCreateSchema}
                    enableReinitialize
                >
                    {renderForm}
                </Formik>
            )}
        </BPContainer>
    );
};
